import { useEffect, useRef, useState } from "react";
import { buildPreview, PREVIEW_SANDBOX } from "@buildlab/preview";
import { familyRoot, lineageTree, type LineageNode } from "@buildlab/codeview";
import type { FileMap } from "@buildlab/shared";
import { api, type GalleryEntry, type ProjectSummary, lessonLabel } from "./api";
import { useFeature } from "./features";
import { GalleryPlayer } from "./GalleryPlayer";
import { GALLERY_CHANGED } from "./ShareReview";
import { SpriteIcon } from "./Sprite";

// The class gallery on Home (feature "gallery", off by default): shared projects with a live preview,
// remixing with credit, and a family tree for one entry. Teachers share a project from here too.

export function Gallery({ teacher, onOpen }: { teacher: boolean; onOpen: (projectId: string) => void }) {
  const on = useFeature("gallery");
  const [entries, setEntries] = useState<GalleryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [tree, setTree] = useState<string | null>(null); // the entry whose family tree is open
  const [busy, setBusy] = useState<string | null>(null);
  const [playing, setPlaying] = useState<(GalleryEntry & { files: FileMap }) | null>(null); // full-screen play (decision 62)
  const [tab, setTab] = useState<string | null>(null); // a lesson's tab (Paul, Oct 21); null = the newest share's lesson

  const load = () =>
    api.gallery().then(
      (r) => setEntries(r.entries),
      (e: Error) => setError(e.message),
    );
  useEffect(() => {
    if (!on) return;
    void load();
    // The teacher showed a held share to the class from "Flagged for you".
    const reload = () => void load();
    window.addEventListener(GALLERY_CHANGED, reload);
    return () => window.removeEventListener(GALLERY_CHANGED, reload);
  }, [on]);
  if (!on) return null;

  // One tab per lesson that has shares, in course order (the Demo, then anything outside the lessons,
  // last), and All. Opens on the lesson of the newest share, so the current lesson comes up first.
  const tabOf = (e: GalleryEntry) => (e.lesson === null ? "other" : String(e.lesson));
  const order = (k: string) => (k === "other" ? 1000 : k === "0" ? 999 : Number(k));
  const tabs = [...new Set((entries ?? []).map(tabOf))].sort((a, b) => order(a) - order(b));
  const current = tab && (tab === "all" || tabs.includes(tab)) ? tab : entries?.[0] ? tabOf(entries[0]) : "all";
  const shown = (entries ?? []).filter((e) => current === "all" || tabOf(e) === current);
  const tabLabel = (k: string) => (k === "other" ? "Other" : lessonLabel(Number(k)));
  const tabbed = tabs.length > 1;

  const remix = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      const { id: projectId } = await api.remixGallery(id);
      onOpen(projectId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't remix that project.");
      setBusy(null);
    }
  };

  const play = async (id: string) => {
    setError(null);
    try {
      setPlaying((await api.galleryEntry(id)).entry);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't open that project.");
    }
  };

  const takeDown = async (id: string) => {
    if (!window.confirm("Take this project down from the class gallery? Remixes of it keep their credit.")) return;
    setError(null);
    try {
      await api.removeFromGallery(id);
      setTree(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't take that down.");
    }
  };

  return (
    <section aria-label="Class gallery" className="gallery">
      <div className="gallery-head">
        <div>
          <h2>Class gallery</h2>
          <p className="muted small">Projects your class shared. Play one, or remix it to start your own version. Share yours with "Share with class" in your project.</p>
        </div>
        {teacher && (
          <button type="button" className="outline small" onClick={() => setSharing((s) => !s)}>
            {sharing ? "Close" : "Share a project"}
          </button>
        )}
      </div>

      {teacher && sharing && (
        <ShareForm
          onShared={async () => {
            setSharing(false);
            await load();
          }}
        />
      )}
      {error && <p className="page-error small gallery-error">{error}</p>}

      {entries === null ? (
        <p className="muted small">Loading the gallery…</p>
      ) : entries.length === 0 ? (
        <p className="muted small">Nothing shared yet.</p>
      ) : (
        <>
          {tabbed && (
            <div className="gal-tabs" role="tablist" aria-label="Gallery lessons">
              {[...tabs, "all"].map((k) => (
                <button key={k} type="button" role="tab" aria-selected={current === k} onClick={() => setTab(k)}>
                  {k === "all" ? "All" : tabLabel(k)}
                  <span className="gal-tab-n">{k === "all" ? entries.length : entries.filter((e) => tabOf(e) === k).length}</span>
                </button>
              ))}
            </div>
          )}
          <ul className="gal-grid" role={tabbed ? "tabpanel" : undefined} aria-label={tabbed ? (current === "all" ? "All shares" : tabLabel(current)) : undefined}>
            {shown.map((e) => (
              <li key={e.id}>
                <GalleryCard
                  entry={e}
                  teacher={teacher}
                  busy={busy === e.id}
                  onRemix={() => remix(e.id)}
                  onPlay={() => play(e.id)}
                  onTree={() => setTree(tree === e.id ? null : e.id)}
                  onTakeDown={() => takeDown(e.id)}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {tree && entries && <FamilyTree entries={entries} id={tree} onClose={() => setTree(null)} />}
      {playing && (
        <GalleryPlayer
          files={playing.files}
          title={playing.title}
          byline={playing.byline}
          galleryId={playing.id}
          remixing={busy === playing.id}
          onRemix={() => remix(playing.id)}
          onClose={() => setPlaying(null)}
        />
      )}
    </section>
  );
}

function GalleryCard({
  entry,
  teacher,
  busy,
  onRemix,
  onPlay,
  onTree,
  onTakeDown,
}: {
  entry: GalleryEntry;
  teacher: boolean;
  busy: boolean;
  onRemix: () => void;
  onPlay: () => void;
  onTree: () => void;
  onTakeDown: () => void;
}) {
  return (
    <article className="gal-card">
      <button type="button" className="gal-play" onClick={onPlay} aria-label={`Play ${entry.title}`}>
        <GalleryThumb id={entry.id} />
      </button>
      <div className="gal-body">
        <h3>
          {entry.title}
          {entry.demo && <span className="badge-demo">Demo</span>}
        </h3>
        <p className="small muted">
          by {entry.avatar && <SpriteIcon id={entry.avatar} size={16} className="gal-avatar" />}
          {entry.byline}
          {entry.className ? ` · ${entry.className}` : ""}
          {entry.remixedFrom && (
            <>
              {" "}
              · remixed from <b>{entry.remixedFrom.title}</b> by {entry.remixedFrom.byline}
            </>
          )}
        </p>
        <div className="row gal-actions">
          <button type="button" className="primary small" onClick={onPlay}>
            Play
          </button>
          <button type="button" className="outline small" disabled={busy} onClick={onRemix}>
            {busy ? "Making your copy…" : "Remix"}
          </button>
          {(entry.remixCount > 0 || entry.remixedFrom) && (
            <button type="button" className="linklike small" onClick={onTree}>
              {entry.remixCount > 0 ? `${entry.remixCount} ${entry.remixCount === 1 ? "remix" : "remixes"} · family tree` : "Family tree"}
            </button>
          )}
          {teacher && (
            <button type="button" className="linklike small gal-down" onClick={onTakeDown}>
              Take down
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

/** A live, scaled-down preview. It loads only once the card scrolls into view, so a class of games doesn't all run at once. */
function GalleryThumb({ id }: { id: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((x) => x.isIntersecting)) {
        setVisible(true);
        io.disconnect();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    let live = true;
    api.galleryEntry(id).then(
      ({ entry }) => {
        if (live) setHtml(buildPreview(entry.files).html);
      },
      () => {},
    );
    return () => {
      live = false;
    };
  }, [visible, id]);

  return (
    <div className="gal-thumb" ref={ref}>
      {html ? <iframe title="Preview of a gallery project" sandbox={PREVIEW_SANDBOX} srcDoc={html} tabIndex={-1} /> : <span className="muted small">Preview</span>}
    </div>
  );
}

function ShareForm({ onShared }: { onShared: () => void | Promise<void> }) {
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [projectId, setProjectId] = useState("");
  const [title, setTitle] = useState("");
  const [byline, setByline] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.projects().then(
      (r) => {
        setProjects(r.projects);
        const first = r.projects[0];
        if (first) {
          setProjectId(first.id);
          setTitle(first.title);
        }
      },
      (e: Error) => setError(e.message),
    );
  }, []);

  const pick = (id: string) => {
    setProjectId(id);
    setTitle(projects?.find((p) => p.id === id)?.title ?? "");
  };

  return (
    <form
      className="gal-share"
      onSubmit={async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
          const r = await api.shareToGallery(projectId, { title, byline });
          if (r.status === "pending") window.alert("Screening wasn't sure about this one, so it's waiting in Flagged for you. Choose Show to class there.");
          await onShared();
        } catch (err) {
          setError(err instanceof Error ? err.message : "Couldn't share that project.");
        } finally {
          setSaving(false);
        }
      }}
    >
      {projects === null ? (
        <p className="muted small">Loading your projects…</p>
      ) : projects.length === 0 ? (
        <p className="muted small">You don't have a project to share yet.</p>
      ) : (
        <>
          <label className="gal-field">
            <span>Project</span>
            <select value={projectId} onChange={(e) => pick(e.target.value)}>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </label>
          <label className="gal-field">
            <span>Title on the card</span>
            <input value={title} maxLength={60} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="gal-field">
            <span>Credit (a first name)</span>
            <input value={byline} maxLength={30} placeholder="Maya" onChange={(e) => setByline(e.target.value)} />
          </label>
          <button type="submit" className="primary small" disabled={saving || !projectId || !byline.trim() || !title.trim()}>
            {saving ? "Sharing…" : "Share with the class"}
          </button>
        </>
      )}
      {error && <p className="page-error small">{error}</p>}
    </form>
  );
}

function FamilyTree({ entries, id, onClose }: { entries: GalleryEntry[]; id: string; onClose: () => void }) {
  const root = familyRoot(entries, id);
  const tree = root ? lineageTree(entries, root.id) : null;
  if (!tree) return null;
  const rows = flatten(tree);
  return (
    <aside className="gal-family" aria-label="Family tree">
      <div className="gallery-head">
        <h3>Family tree</h3>
        <button type="button" className="linklike small" onClick={onClose}>
          Close
        </button>
      </div>
      <ol className="gal-tree">
        {rows.map(({ entry, depth }) => (
          <li key={entry.id} className={entry.id === id ? "now" : undefined} style={{ marginLeft: `${depth * 1.4}rem` }}>
            {depth > 0 && <span aria-hidden="true">↳ </span>}
            <b>{entry.title}</b> by {entry.byline}
            {entry.id === id && <span className="small muted"> (this one)</span>}
          </li>
        ))}
      </ol>
    </aside>
  );
}

/** The tree as rows in order, each with its depth (how many remixes deep). */
function flatten<T>(node: LineageNode<T>, depth = 0): { entry: T; depth: number }[] {
  return [{ entry: node.entry, depth }, ...node.children.flatMap((c) => flatten(c, depth + 1))];
}
